// read-more article on react-query optimistic-updates
// https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FeedsQueryKeys } from './feeds.keys';
import { PostInterface } from '../../app-schemas/posts/post.schema';
import apiClient from '../ApiClient';

// API call to save a feed post by its ID
const saveFeedById = async (id: number): Promise<any> => {
  return await apiClient.post('bookmark/create', { community_post_id: id });
};

export const useSaveFeedById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveFeedById,

    onMutate: async (post_id: number) => {
      // Cancel ongoing fetches to prevent overriding the optimistic update
      await queryClient.cancelQueries({ queryKey: FeedsQueryKeys.all });

      // Retrieve the current cached data for the feed
      const previousData: any = queryClient.getQueryData(FeedsQueryKeys.all);
      if (!previousData) return { previousData };

      // Optimistically update the post reactions
      const updatedPages = previousData?.pages.map((page: any) => {
        const updatedPosts = page?.data.map((post: PostInterface) => {
          if (post.post_id === post_id) {
            return {
              ...post,
              bookmarked_post: !post.bookmarked_post,
            };
          }

          return post;
        });

        return { ...page, data: updatedPosts };
      });

      const newPagesData = {
        ...previousData,
        pages: updatedPages,
      };

      queryClient.setQueryData(FeedsQueryKeys.all, newPagesData);

      // Return context with previous data in case of rollback
      return { previousData };
    },
    onError: (err, _, context?: any) => {
      // Rollback to the previous data on error
      if (context?.previousData) {
        queryClient.setQueryData(FeedsQueryKeys.all, context.previousData);
      }
    },
  });
};
